import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Paper, Tabs, Tab, Typography, Container, Box } from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function CottonFieldDataTab() {
  const [fieldDataSubTab, setFieldDataSubTab] = useState(0);
  const [plantTissueData, setPlantTissueData] = useState([]);
  const [soilData, setSoilData] = useState([]);
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");
  const soilChartScrollRef = useRef(null);
  const plantTissueChartScrollRef = useRef(null);

  useEffect(() => {
    if (fieldDataSubTab === 0) fetchSoilData();
    else fetchPlantTissueData();
    // eslint-disable-next-line
  }, [fieldDataSubTab]);

  const fetchSoilData = async () => {
    try {
      const response = await axios.get("/api/getSoilData", {
        params: { teamName: username, cropType: "cotton" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSoilData(response.data.data || []);
    } catch (error) {
      setSoilData([]);
    }
  };

  const fetchPlantTissueData = async () => {
    try {
      const response = await axios.get("/api/getPlantTissueData", {
        params: { teamName: username, cropType: "cotton" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlantTissueData(response.data.data || []);
    } catch (error) {
      setPlantTissueData([]);
    }
  };

  return (
    <Container component="main" maxWidth="90%">
      <Paper elevation={3} style={{ marginBottom: "20px", marginTop: "20px" }}>
        <Tabs
          value={fieldDataSubTab}
          onChange={(event, newValue) => setFieldDataSubTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Soil Data" />
          <Tab label="Plant Tissue Data" />
        </Tabs>
      </Paper>
      {fieldDataSubTab === 0 && (
        <Paper elevation={2} style={{ padding: "30px", borderRadius: "10px" }}>
          <Typography
            variant="h6"
            gutterBottom
            style={{ fontWeight: "bold", marginBottom: "20px" }}
          >
            Soil Nitrate Levels (NO3-N) by Depth
          </Typography>
          {soilData.length > 0 ? (
            <div
              ref={soilChartScrollRef}
              style={{
                width: "100%",
                overflowX: window.innerWidth < 768 ? "auto" : "visible",
              }}
            >
              <ResponsiveContainer
                width={window.innerWidth < 768 ? soilData.length * 150 : "100%"}
                height={450}
              >
                <LineChart
                  data={[...soilData].reverse().map((item) => {
                    const dateStr = item.date.split("T")[0];
                    const [year, month, day] = dateStr.split("-");
                    const displayDate = new Date(
                      year,
                      month - 1,
                      day,
                    ).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    });
                    return {
                      date: displayDate,
                      "0-6in": parseFloat(item["NO3-N_0_6"]) || 0,
                      "6-12in": parseFloat(item["NO3-N_6_12"]) || 0,
                    };
                  })}
                  margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis
                    label={{
                      value: "NO3-N (ppm)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                    stroke="#666"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="linear"
                    dataKey="0-6in"
                    stroke="#1f77b4"
                    name="0-6 inches"
                    strokeWidth={3}
                    dot={{ fill: "#1f77b4", r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                  <Line
                    type="linear"
                    dataKey="6-12in"
                    stroke="#ff7f0e"
                    name="6-12 inches"
                    strokeWidth={3}
                    dot={{ fill: "#ff7f0e", r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={450}
            >
              <Typography variant="h6" color="textSecondary">
                No soil data available yet
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      {fieldDataSubTab === 1 && (
        <Paper elevation={2} style={{ padding: "30px", borderRadius: "10px" }}>
          <Typography
            variant="h6"
            gutterBottom
            style={{ fontWeight: "bold", marginBottom: "20px" }}
          >
            Plant Tissue Nutrient Analysis
          </Typography>
          {plantTissueData.length > 0 ? (
            <div
              ref={plantTissueChartScrollRef}
              style={{
                width: "100%",
                overflowX: window.innerWidth < 768 ? "auto" : "visible",
              }}
            >
              <ResponsiveContainer
                width={
                  window.innerWidth < 768
                    ? plantTissueData.length * 200
                    : "100%"
                }
                height={450}
              >
                <BarChart
                  data={[...plantTissueData].reverse().map((item) => {
                    const dateStr = item.date.split("T")[0];
                    const [year, month, day] = dateStr.split("-");
                    const displayDate = new Date(
                      year,
                      month - 1,
                      day,
                    ).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    });
                    return {
                      date: displayDate,
                      N: parseFloat(item.Nitrogen) || 0,
                      P: parseFloat(item.Phosphorus) || 0,
                      K: parseFloat(item.Potassium) || 0,
                    };
                  })}
                  margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
                  barCategoryGap="35%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis
                    label={{
                      value: "Percentage (%)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                    stroke="#666"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                    }}
                  />
                  <Bar
                    dataKey="N"
                    fill="#3b82f6"
                    name="N (%)"
                    radius={[8, 8, 0, 0]}
                    barSize={35}
                  />
                  <Bar
                    dataKey="P"
                    fill="#10b981"
                    name="P (%)"
                    radius={[8, 8, 0, 0]}
                    barSize={35}
                  />
                  <Bar
                    dataKey="K"
                    fill="#f59e0b"
                    name="K (%)"
                    radius={[8, 8, 0, 0]}
                    barSize={35}
                  />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={450}
            >
              <Typography variant="h6" color="textSecondary">
                No plant tissue data available yet
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
}

export default CottonFieldDataTab;
